/**
 * Fleet UX: one source of truth for how fleets are categorized, labeled, filtered and sorted —
 * used by the fleet manager, the system panel and the galaxy map.
 *
 * The category is derived from the actual ship composition (no stored field, no migration):
 *   scout → survey, construction → construction, corvette/frigate/cruiser → military;
 *   ships of more than one kind → mixed; no ships → empty.
 * Category and status are separate: a survey fleet can be idle, traveling or surveying.
 * "Reserve" is the fleet's role (an existing field), shown as a neutral badge next to the category.
 */
import type { FleetClient } from '@galaxis/shared';

export type FleetCategory = 'military' | 'survey' | 'construction' | 'mixed' | 'empty';
export type ShipKind = 'military' | 'survey' | 'construction';

export const SHIP_KIND: Record<string, ShipKind> = {
  scout: 'survey',
  construction: 'construction',
  corvette: 'military',
  frigate: 'military',
  cruiser: 'military',
};

export interface CategoryMeta {
  label: string;
  /** Short plural label for filters */
  filterLabel: string;
  /** CSS color */
  color: string;
  /** Same color for PixiJS */
  hex: number;
}

export const CATEGORY_META: Record<FleetCategory, CategoryMeta> = {
  military: { label: 'Harci flotta', filterLabel: 'Harci', color: '#ef4444', hex: 0xef4444 },
  survey: { label: 'Kutatóflotta', filterLabel: 'Kutató', color: '#38bdf8', hex: 0x38bdf8 },
  construction: { label: 'Építőflotta', filterLabel: 'Építő', color: '#f59e0b', hex: 0xf59e0b },
  mixed: { label: 'Vegyes flotta', filterLabel: 'Vegyes', color: '#a78bfa', hex: 0xa78bfa },
  empty: { label: 'Üres flotta', filterLabel: 'Üres', color: '#94a3b8', hex: 0x94a3b8 },
};

export const RESERVE_COLOR = '#94a3b8';

/** Kinds present in a composition (ship class → count), in a stable order. */
export function shipKinds(shipCounts: Record<string, number>): ShipKind[] {
  const present = new Set<ShipKind>();
  for (const [cls, n] of Object.entries(shipCounts)) {
    const kind = SHIP_KIND[cls];
    if (kind && n > 0) present.add(kind);
  }
  return (['military', 'survey', 'construction'] as const).filter(k => present.has(k));
}

export function categoryFromCounts(shipCounts: Record<string, number>): FleetCategory {
  const kinds = shipKinds(shipCounts);
  if (kinds.length === 0) return 'empty';
  return kinds.length === 1 ? kinds[0]! : 'mixed';
}

/** Works for full fleets and for the minimal fleet shape the system endpoint returns (ships only). */
export function categoryOf(fleet: { ships: Array<{ class: string }>; stats?: { shipCounts: Record<string, number> } }): FleetCategory {
  if (fleet.stats) return categoryFromCounts(fleet.stats.shipCounts);
  const counts: Record<string, number> = {};
  for (const s of fleet.ships) counts[s.class] = (counts[s.class] ?? 0) + 1;
  return categoryFromCounts(counts);
}

/** Destroyed or disbanded fleets never count and never show in the active list. */
export const isActiveFleet = (f: Pick<FleetClient, 'status'>) => f.status !== 'destroyed' && f.status !== 'disbanded';

// ---- filtering, search, sorting ----

export type CategoryFilter = 'all' | 'military' | 'survey' | 'construction' | 'mixed';
export const CATEGORY_FILTERS: CategoryFilter[] = ['all', 'military', 'survey', 'construction', 'mixed'];
export type StatusFilter = 'any' | 'idle' | 'moving' | 'busy' | 'combat' | 'automated';
export const STATUS_FILTER_LABELS: Record<StatusFilter, string> = {
  any: 'Bármilyen állapot',
  idle: 'Várakozik',
  moving: 'Úton',
  busy: 'Felmér / épít',
  combat: 'Harcban',
  automated: 'Automatizált',
};
export type SortKey = 'name' | 'location' | 'strength' | 'activity' | 'eta';
export const SORT_LABELS: Record<SortKey, string> = {
  name: 'Név szerint',
  location: 'Helyszín szerint',
  strength: 'Harci erő szerint',
  activity: 'Aktivitás szerint',
  eta: 'Érkezés szerint',
};

/** A fleet with everything the list needs precomputed once per data change (not per render). */
export interface FleetRow {
  fleet: FleetClient;
  category: FleetCategory;
  /** lower-cased name for search */
  key: string;
  location: string;
}

export function toRows(fleets: FleetClient[], systemName: (id: string) => string): FleetRow[] {
  return fleets.filter(isActiveFleet).map(fleet => ({
    fleet, category: categoryOf(fleet), key: fleet.name.toLocaleLowerCase('hu'), location: systemName(fleet.currentSystemId),
  }));
}

export function matchesStatus(f: FleetClient, s: StatusFilter): boolean {
  switch (s) {
    case 'any': return true;
    case 'idle': return f.status === 'idle';
    case 'moving': return f.status === 'traveling';
    case 'busy': return f.status === 'surveying' || f.status === 'constructing';
    case 'combat': return f.status === 'in_combat';
    case 'automated': return f.commands.automation !== null;
  }
}

export function categoryCounts(rows: FleetRow[]): Record<CategoryFilter, number> {
  const c: Record<CategoryFilter, number> = { all: rows.length, military: 0, survey: 0, construction: 0, mixed: 0 };
  for (const r of rows) if (r.category !== 'empty') c[r.category]++;
  return c;
}

/** Busy fleets first (combat, travel, work, automation), then idle ones. */
function activityRank(f: FleetClient): number {
  if (f.status === 'in_combat') return 0;
  if (f.status === 'traveling') return 1;
  if (f.status === 'surveying' || f.status === 'constructing') return 2;
  if (f.commands.automation || f.commands.active) return 3;
  return 4;
}

/** Traveling fleets by arrival time (soonest first), then everything else by name. */
function etaKey(f: FleetClient): number {
  return f.status === 'traveling' && f.eta ? new Date(f.eta).getTime() : Number.POSITIVE_INFINITY;
}

export function selectRows(rows: FleetRow[], o: { category: CategoryFilter; status: StatusFilter; query: string; sort: SortKey; group?: string }): FleetRow[] {
  const q = o.query.trim().toLocaleLowerCase('hu');
  const group = o.group ?? 'any';
  const out = rows.filter(r =>
    (o.category === 'all' || r.category === o.category) && matchesStatus(r.fleet, o.status) && (!q || r.key.includes(q))
    && (group === 'any' || (group === 'none' ? !r.fleet.groupId : r.fleet.groupId === group)));
  const byName = (a: FleetRow, b: FleetRow) => a.fleet.name.localeCompare(b.fleet.name, 'hu');
  const cmp: Record<SortKey, (a: FleetRow, b: FleetRow) => number> = {
    name: byName,
    location: (a, b) => a.location.localeCompare(b.location, 'hu') || byName(a, b),
    strength: (a, b) => b.fleet.stats.militaryStrength - a.fleet.stats.militaryStrength || byName(a, b),
    activity: (a, b) => activityRank(a.fleet) - activityRank(b.fleet) || byName(a, b),
    eta: (a, b) => { const x = etaKey(a.fleet), y = etaKey(b.fleet); return x === y ? byName(a, b) : x < y ? -1 : 1; },
  };
  return out.sort(cmp[o.sort]);
}

// ---- actions (mirror the server rules; the server still decides) ----

export type FleetActionId = 'move' | 'survey' | 'auto_survey' | 'outpost' | 'station' | 'attack' | 'retreat' | 'patrol' | 'show_on_map';

export interface FleetAction { id: FleetActionId; label: string; reason: string | null }

export interface ActionContext {
  /** discovery status of the current system for this player */
  currentDiscovery: 'unknown' | 'detected' | 'surveyed' | 'colonized' | undefined;
  /** the current system belongs to this player */
  currentOwnedByMe: boolean;
  /** the current system belongs to anyone */
  currentOwned: boolean;
  /** known neighbor systems */
  neighborCount: number;
  /** this fleet occupies an enemy system right now */
  occupying: boolean;
}

export const ACTION_LABELS: Record<FleetActionId, string> = {
  move: '🚀 Mozgatás',
  survey: '🔭 Felmérés',
  auto_survey: '🤖 Automatikus felfedezés',
  outpost: '🏗️ Előőrs építése',
  station: '⚙️ Állomásépítés',
  attack: '⚔️ Támadás',
  retreat: '🏳️ Visszavonulás',
  patrol: '🔁 Járőrözés',
  show_on_map: '🗺️ Mutatás a térképen',
};

/**
 * Category-dependent actions. Which ones are shown follows the composition (a mixed fleet gets the
 * union of what its ships can do); whether they are enabled follows the same rules the server
 * enforces — with a reason when not.
 */
export function fleetActions(f: FleetClient, ctx: ActionContext): FleetAction[] {
  const idle = f.status === 'idle';
  const busy = idle ? null : 'A flotta nem szabad.';
  const automation = f.commands.automation ? 'A flottának már van automatizálása — előbb állítsd le.' : null;
  const out: FleetAction[] = [];
  const add = (id: FleetActionId, reason: string | null) => out.push({ id, label: ACTION_LABELS[id], reason });

  add('move', busy ?? (ctx.neighborCount === 0 ? 'Nincs ismert szomszédos rendszer.' : null));
  if (f.capabilities.survey) {
    add('survey', busy ?? (ctx.currentDiscovery !== 'detected' ? 'Ez a rendszer már fel van mérve.' : null));
    add('auto_survey', automation);
  }
  if (f.capabilities.construction) {
    const surveyed = ctx.currentDiscovery === 'surveyed' || ctx.currentDiscovery === 'colonized';
    add('outpost', busy ?? (!surveyed ? 'Előbb fel kell mérni a rendszert.' : ctx.currentOwned ? 'A rendszernek már van tulajdonosa.' : null));
    add('station', busy ?? (!ctx.currentOwnedByMe ? 'Állomás csak saját rendszerben építhető.' : null));
  }
  if (f.capabilities.military) {
    add('attack', busy);
    if (ctx.occupying) add('retreat', null);
    add('patrol', automation);
  }
  add('show_on_map', null);
  return out;
}

/** Where the fleet is on the map right now (the destination while traveling). */
export function mapFocusSystem(f: FleetClient): string {
  return f.status === 'traveling' && f.destinationSystemId ? f.destinationSystemId : f.currentSystemId;
}

/** Fraction 0..1 of the active order (travel or survey) done at `now`, or null. */
export function orderProgress(f: FleetClient, now: number): number | null {
  const o = f.activeOrder;
  const end = o?.arrivesAt ?? o?.completesAt ?? f.eta;
  if (!o || !end) return null;
  const start = new Date(o.issuedAt).getTime();
  const stop = new Date(end).getTime();
  if (!(stop > start)) return null;
  return Math.max(0, Math.min(1, (now - start) / (stop - start)));
}

/** Own fleets grouped per system for the map: count and categories (traveling fleets excluded). */
export function fleetsBySystem(fleets: FleetClient[]): Map<string, { count: number; categories: FleetCategory[]; ids: string[] }> {
  const m = new Map<string, { count: number; categories: FleetCategory[]; ids: string[] }>();
  for (const f of fleets) {
    if (!isActiveFleet(f) || f.status === 'traveling') continue;
    const e = m.get(f.currentSystemId) ?? { count: 0, categories: [], ids: [] };
    e.count++;
    e.ids.push(f.id);
    const c = categoryOf(f);
    if (!e.categories.includes(c)) e.categories.push(c);
    m.set(f.currentSystemId, e);
  }
  return m;
}
